"use client";

import { ReactNode, useEffect } from "react";
import { X } from "lucide-react";
import { cn } from "../utils";

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    children: ReactNode;
    size?: "sm" | "md" | "lg" | "xl";
}

export const Modal = ({ isOpen, onClose, title, children, size = "md" }: ModalProps) => {
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => { document.body.style.overflow = ""; };
    }, [isOpen]);

    if (!isOpen) return null;

    const sizeClasses = {
        sm: "max-w-sm",
        md: "max-w-lg",
        lg: "max-w-2xl",
        xl: "max-w-4xl",
    };

    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-title"
                className={cn(
                "relative flex w-full flex-col border border-border bg-card text-card-foreground shadow-premium animate-in fade-in duration-200",
                "max-h-[85vh] sm:max-h-[90vh]",
                "rounded-t-2xl sm:rounded-2xl",
                "slide-in-from-bottom-4 sm:zoom-in-95",
                sizeClasses[size]
            )}>
                <div className="flex items-center justify-between border-b border-border px-4 py-3.5 sm:px-6 sm:py-4 shrink-0">
                    <h2 id="modal-title" className="text-base sm:text-lg font-semibold text-foreground pr-2">{title}</h2>
                    <button
                        onClick={onClose}
                        className="flex h-10 w-10 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground shrink-0"
                        aria-label="Close dialog"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto overscroll-contain p-4 pb-20 sm:p-6 sm:pb-6">
                    {children}
                </div>
            </div>
        </div>
    );
};
